import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";

// Common parameterized PDF/Excel export module for any list/report page in
// the app - not just Stock. A page hands over its own data plus a plain
// column-accessor list (no JSX, no antd `render` functions) and gets a
// letterhead PDF or an Excel file back; nothing here knows anything about
// Stock/LPO/etc specifically. Modeled on the shape of a working reference
// (LoungeWebApp's reportUtils.js: columns + data + totals + title/dates
// driving one shared export function) but rebuilt clean - that version's
// per-field switch-statement value mapping is replaced here with a plain
// `value: (row) => string` accessor per column, which every caller already
// has lying around as its antd column `render` logic, just returning text
// instead of JSX.
//
// column shape: { header: string, value: (row) => string|number, align?: "left"|"right" }
// totals shape (optional): { [header]: string|number } - only headers present
// get a value in the totals row, everything else is blank.

// Each real branch (MOMBASA/NAIROBI/SYSTEMS/TEXASTRACK) has its own distinct
// letterhead logo (legacy WinForms report convention) - same 4 assets and
// the same MOMBASA-as-default fallback as Flutter's quotation_pdf_builder.dart
// `_logoAssetFor`, so a Stock export looks consistent with the sales
// quotation PDF a branch already produces.
const LOGO_BY_BRANCH_CODE = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};
const logoAssetFor = (branchCode) => LOGO_BY_BRANCH_CODE[branchCode] || logoMombasa;

const logoDataUrlCache = {};
const getLogoDataUrl = (branchCode) =>
  new Promise((resolve) => {
    const asset = logoAssetFor(branchCode);
    if (logoDataUrlCache[asset]) {
      resolve(logoDataUrlCache[asset]);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg");
      logoDataUrlCache[asset] = dataUrl;
      resolve(dataUrl);
    };
    // A broken/unloadable logo shouldn't block the export - fall back to a
    // text-only header instead of rejecting the whole export.
    img.onerror = () => resolve(null);
    img.src = asset;
  });

const rowsToAoa = (columns, rows) => rows.map((row) => columns.map((c) => c.value(row) ?? ""));

const totalsToRow = (columns, totals) =>
  totals ? columns.map((c) => (c.header in totals ? totals[c.header] : "")) : null;

// A groupBy key may carry two parts separated by this delimiter - e.g. Stock
// Movement Report's "ProductGroup||Customer Name" when grouped by Product
// Group under a customer search (see StockMovementReportPage.jsx's
// exportGroupBy). Lets the group heading show both a primary label (left)
// and a secondary one (right) in one row - here specifically the resolved
// full customer name, so a group heading disambiguates even when a
// partial/substring customer search matched more than one real customer.
// Plain single-value keys (the common case - Type, Product Group alone,
// Party/Reference alone) never contain this and render exactly as before.
const GROUP_KEY_SPLIT = "||";
const splitGroupKey = (key) => {
  const idx = key.indexOf(GROUP_KEY_SPLIT);
  return idx === -1 ? [key, null] : [key.slice(0, idx), key.slice(idx + GROUP_KEY_SPLIT.length)];
};

// Optional grouping (e.g. Stock Balances by Product Group, matching the
// legacy "Stock Summary" report - one bold group-name row, then that
// group's rows, groups and rows both alphabetical) - a plain
// `groupBy: (row) => string` accessor, same shape convention as `columns`.
// `groupOrder` (optional array of keys): those groups come first, in that
// order, for groupings whose natural order isn't alphabetical (e.g. the
// Technician Duty Report's Started -> On hold -> Completed, the same order the
// legacy "Daily Work Report" printed). Any key not listed follows
// alphabetically; omitted = plain alphabetical, as before.
const groupRows = (rows, groupBy, groupOrder) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = groupBy(row) || "Ungrouped";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  const rank = (key) => {
    const i = groupOrder ? groupOrder.indexOf(key) : -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...map.keys()]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((key) => ({ key, rows: map.get(key) }));
};

// A column's `value()` already returns display-ready text (e.g. money()'s
// "1,234.00"), not a raw number - parsed back out here so group/grand
// totals can be summed without every caller also having to hand over a
// separate numeric accessor per column.
const numericColumnValue = (column, row) => {
  const raw = column.value(row);
  if (typeof raw === "number") return raw;
  const n = Number(String(raw ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// One total row for a given set of rows: `label` in the first column, the
// sum of that set's own values in every `align: "right"` (numeric) column,
// blank everywhere else. Used for both a per-group subtotal (groupTotals
// option below) and the final grand total.
const totalRowFor = (columns, rowsForTotal, label) =>
  columns.map((c, i) => {
    if (i === 0) return label;
    if (c.align !== "right") return "";
    return rowsForTotal.reduce((sum, row) => sum + numericColumnValue(c, row), 0).toFixed(2);
  });

// title/subtitleLines/companyName form the letterhead block shown above the
// table on both export formats, matching what an LPO/Stock document print
// already uses (company name + logo, see StockDocumentPreview.jsx) so a
// filtered list export looks consistent with the rest of the app's paper
// output rather than inventing a second style.
export const exportListToPdf = async ({
  title,
  subtitleLines = [],
  companyName = "TEXAS ALARMS (K) LTD",
  // Which branch's letterhead logo to use - pass the branch filter's
  // selected branchCode when the export is scoped to one branch; omit (or
  // pass a mixed/"all branches" export) to fall back to the Mombasa mark,
  // same convention as the Flutter quotation PDF.
  branchCode,
  columns,
  rows,
  totals,
  fileName,
  // Optional: (row) => string. Groups rows under a bold section-header row
  // per distinct value, sorted alphabetically (groups and rows within a
  // group alike) - e.g. Stock Balances grouping by Product Group.
  groupBy,
  // Optional, only meaningful together with groupBy: adds a "Subtotal" row
  // after each group (summing that group's own `align: "right"` columns)
  // plus one final "Grand Total" row summing every row - opt-in per caller
  // so existing groupBy exports (Products, Stock Balances) are unaffected.
  groupTotals = false,
  // Optional - see groupRows.
  groupOrder,
  // "landscape" for a wide report (many text columns); portrait by default.
  orientation = "portrait",
}) => {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const logoDataUrl = await getLogoDataUrl(branchCode);

  let cursorY = 14;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "JPEG", 14, 10, 22, 22);
  }
  const textX = logoDataUrl ? 40 : 14;
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text(companyName, textX, cursorY);
  cursorY += 6;
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.text(title, textX, cursorY);
  cursorY += 6;
  doc.setFontSize(9);
  subtitleLines.forEach((line) => {
    doc.text(line, textX, cursorY);
    cursorY += 4.5;
  });

  const subtotalRowStyles = { fontStyle: "bold", fillColor: [240, 240, 240], textColor: 20 };
  const groupHeaderStyles = { fontStyle: "bold", fillColor: [225, 225, 225], textColor: 20 };
  // A two-part key ("left||right") splits across two cells instead of one
  // spanning cell, so the secondary value (e.g. the resolved customer name)
  // reads on the right side of the same bold bar rather than prefixed onto
  // the left - see splitGroupKey above.
  const groupHeaderRow = (key) => {
    const [left, right] = splitGroupKey(key);
    if (right === null) {
      return [{ content: left, colSpan: columns.length, styles: groupHeaderStyles }];
    }
    return [
      { content: left, colSpan: columns.length - 1, styles: groupHeaderStyles },
      { content: right, styles: { ...groupHeaderStyles, halign: "right" } },
    ];
  };
  const startY = Math.max(cursorY + 3, 36);
  const groups = groupBy ? groupRows(rows, groupBy, groupOrder) : null;
  const body = groups
    ? groups.flatMap(({ key, rows: groupedRows }) => [
        groupHeaderRow(key),
        ...rowsToAoa(columns, groupedRows),
        ...(groupTotals
          ? [totalRowFor(columns, groupedRows, "Subtotal").map((content) => ({ content, styles: subtotalRowStyles }))]
          : []),
      ])
    : rowsToAoa(columns, rows);
  if (groups && groupTotals && groups.length > 1) {
    body.push(
      totalRowFor(columns, rows, "Grand Total").map((content) => ({
        content,
        styles: { ...subtotalRowStyles, fillColor: [225, 225, 225] },
      }))
    );
  }
  autoTable(doc, {
    startY,
    head: [columns.map((c) => c.header)],
    body,
    foot: totals ? [totalsToRow(columns, totals)] : undefined,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [33, 37, 41] },
    footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.align === "right") acc[i] = { halign: "right" };
      return acc;
    }, {}),
    margin: { left: 14, right: 14 },
  });

  doc.setFontSize(8);
  doc.text(
    `Generated ${new Date().toLocaleString()}`,
    14,
    doc.internal.pageSize.getHeight() - 8
  );

  doc.save(`${fileName || title}.pdf`);
};

export const exportListToExcel = ({
  title,
  subtitleLines = [],
  companyName = "TEXAS ALARMS (K) LTD",
  columns,
  rows,
  totals,
  fileName,
  groupBy,
  // See exportListToPdf's groupTotals - same opt-in per-group Subtotal +
  // final Grand Total, plain rows here instead of styled autoTable cells.
  groupTotals = false,
  groupOrder,
}) => {
  const preamble = [[companyName], [title], ...subtitleLines.map((l) => [l]), []];
  const header = columns.map((c) => c.header);
  // Two-part key ("left||right", see splitGroupKey) - left in the first
  // cell, right pushed out to the last column so it reads on the right side
  // of the row, same left/right split as the PDF's spanning header cells.
  const groupHeaderRow = (key) => {
    const [left, right] = splitGroupKey(key);
    if (right === null) return [left];
    const row = new Array(columns.length).fill("");
    row[0] = left;
    row[columns.length - 1] = right;
    return row;
  };
  const groups = groupBy ? groupRows(rows, groupBy, groupOrder) : null;
  const body = groups
    ? groups.flatMap(({ key, rows: groupedRows }) => [
        groupHeaderRow(key),
        ...rowsToAoa(columns, groupedRows),
        ...(groupTotals ? [totalRowFor(columns, groupedRows, "Subtotal")] : []),
      ])
    : rowsToAoa(columns, rows);
  if (groups && groupTotals && groups.length > 1) {
    body.push(totalRowFor(columns, rows, "Grand Total"));
  }
  const totalsRow = totalsToRow(columns, totals);
  const aoa = [...preamble, header, ...body, ...(totalsRow ? [totalsRow] : [])];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileName || title}.xlsx`);
};

export const sumField = (rows, field) => rows.reduce((s, r) => s + (Number(r[field]) || 0), 0);
